export interface County {
  countyFIPS: string;
  countyName: string;
  state: { label: string; stateName: string; abbreviation: string };
}

export interface CountyAddress {
  zipCode: string;
  countyName: string;
  fipsCode: string;
  state: string;
}

export type CountySelectionState =
  | { status: 'empty' | 'incomplete'; zip: string }
  | { status: 'loading'; zip: string; saved: CountyAddress | null }
  | { status: 'needsSelection'; zip: string; counties: County[] }
  | { status: 'selected'; zip: string; counties: County[]; county: County }
  | { status: 'invalidZip'; zip: string }
  | { status: 'lookupFailed'; zip: string; saved: CountyAddress | null };

export type CountySelectionEvent =
  | { type: 'addressLoaded'; address: CountyAddress }
  | { type: 'zipEdited'; zip: string }
  | { type: 'lookupSucceeded'; zip: string; counties: County[] }
  | { type: 'lookupFailed'; zip: string }
  | { type: 'countySelected'; fips: string }
  | { type: 'retry' };

export const emptyCountySelection: CountySelectionState = { status: 'empty', zip: '' };

function startLookup(zip: string, saved: CountyAddress | null): CountySelectionState {
  if (!zip) return { status: 'empty', zip };
  if (!/^\d{5}$/.test(zip)) return { status: 'incomplete', zip };
  return { status: 'loading', zip, saved };
}

export function countySelectionReducer(state: CountySelectionState, event: CountySelectionEvent): CountySelectionState {
  switch (event.type) {
    case 'addressLoaded':
      return startLookup(event.address.zipCode, event.address);
    case 'zipEdited':
      return event.zip === state.zip ? state : startLookup(event.zip, null);
    case 'retry':
      return state.status === 'lookupFailed' ? startLookup(state.zip, state.saved) : state;
    case 'lookupFailed':
      return state.status === 'loading' && state.zip === event.zip
        ? { status: 'lookupFailed', zip: state.zip, saved: state.saved }
        : state;
    case 'lookupSucceeded': {
      if (state.status !== 'loading' || state.zip !== event.zip) return state;
      const { counties } = event;
      if (!counties.length) return { status: 'invalidZip', zip: state.zip };
      const saved = state.saved;
      const matchingCounties = saved?.fipsCode
        ? counties.filter(item => item.countyFIPS === saved.fipsCode)
        : counties.filter(
            item =>
              item.countyName === saved?.countyName &&
              (!saved.state || [item.state.stateName, item.state.label, item.state.abbreviation].includes(saved.state)),
          );
      const county = matchingCounties.length === 1 ? matchingCounties[0] : counties.length === 1 ? counties[0] : null;
      return county
        ? { status: 'selected', zip: state.zip, counties, county }
        : { status: 'needsSelection', zip: state.zip, counties };
    }
    case 'countySelected': {
      if (state.status !== 'selected' && state.status !== 'needsSelection') return state;
      const county = state.counties.find(item => item.countyFIPS === event.fips);
      return county ? { status: 'selected', zip: state.zip, counties: state.counties, county } : state;
    }
  }
}
